import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { DropdownComponent } from '@catch/general-ui';
import { LanguageService, languages } from '@catch/services';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowRightArrowLeft, faGlobe } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, startWith } from 'rxjs/operators';
import { PlatformApplyWithProfileModalComponent } from '../../../platform/components/apply-with-profile-modal/platform-apply-with-profile-modal.component';
import { PlatformAuthModalPanel } from '../../../platform/enums/platform-auth-modal-panel.enum';
import { PlatformAuthService } from '../../../platform/services/platform-auth.service';
import { PlatformJobboardLoginModalService } from '../../../platform/services/platform-jobboard-login-modal.service';
import { ColorService } from '../../../services/color.service';
import { EmbedService } from '../../../services/embed.service';
import { FontawesomeService } from '../../../services/fontawesome.service';
import { SEOService } from '../../../services/seo.service';
import { isJobboardJobRoute } from '../../../utils/platform-route.util';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    FaIconComponent,
    TranslocoPipe,
    DropdownComponent,
    PlatformApplyWithProfileModalComponent,
  ],
})
export class HeaderComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  public readonly colorService = inject(ColorService);
  public readonly embedService = inject(EmbedService);
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly languageService = inject(LanguageService);
  public readonly seoService = inject(SEOService);
  private readonly auth = inject(PlatformAuthService);
  private readonly authModal = inject(PlatformJobboardLoginModalService);

  readonly loggedIn = this.auth.loggedIn;
  readonly languages = languages;
  readonly icons = {
    globe: faGlobe,
    switch: faArrowRightArrowLeft,
  };
  readonly showWebsite = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => isJobboardJobRoute(this.router.url)),
      startWith(isJobboardJobRoute(this.router.url))
    ),
    { initialValue: isJobboardJobRoute(this.router.url) }
  );

  constructor() {
    this.colorService.changeEvent$.pipe(untilDestroyed(this)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  /**
   * Opens the jobboard login modal and sends the user to the platform dashboard after login.
   */
  openLogin(): void {
    this.authModal.open({
      panel: PlatformAuthModalPanel.LOGIN,
      redirectUrl: null,
    });
  }
}
